import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AppConfig } from '../types';
import { DEFAULT_CONFIG } from '../utils/constants';
import { apiClient } from '../services/apiClient';
import { useAuth } from './AuthContext';

interface ConfigContextType {
  config: AppConfig;
  isLoading: boolean;
  error: string | null;
  setSMMLV: (value: number) => Promise<void>;
  setExchangeRate: (value: number) => Promise<void>;
  setGAPercentage: (value: number) => Promise<void>;
  setHighSalaryMultiplier: (value: number) => Promise<void>;
  setIntegralMultiplier: (value: number) => Promise<void>;
  setOperationalCosts: (costs: AppConfig['operationalCosts']) => Promise<void>;
  resetConfig: () => Promise<void>;
  refreshConfig: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig>(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  // Load config from API when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadConfig();
    }
  }, [isAuthenticated]);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.getConfig();
      setConfig(response.config);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load config';
      setError(message);
      console.error('Failed to load config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfig = async (updates: Partial<AppConfig>) => {
    try {
      setError(null);
      const response = await apiClient.updateConfig(updates);
      setConfig(response.config);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update config';
      setError(message);
      throw error;
    }
  };

  const value: ConfigContextType = {
    config,
    isLoading,
    error,
    setSMMLV: async (value: number) => updateConfig({ smmlv: value }),
    setExchangeRate: async (value: number) => updateConfig({ exchangeRate: value }),
    setGAPercentage: async (value: number) => updateConfig({ gaPercentage: value }),
    setHighSalaryMultiplier: async (value: number) => updateConfig({ highSalaryMultiplier: value }),
    setIntegralMultiplier: async (value: number) => updateConfig({ integralMultiplier: value }),
    setOperationalCosts: async (costs: AppConfig['operationalCosts']) => updateConfig({ operationalCosts: costs }),
    resetConfig: async () => {
      try {
        setError(null);
        const response = await apiClient.resetConfig();
        setConfig(response.config);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to reset config';
        setError(message);
        throw error;
      }
    },
    refreshConfig: loadConfig,
  };

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}
